import { z } from 'zod'

export const CustomDomain = z.object({
  id: z.string(),
  workspaceId: z.string(),
  hostname: z.string(),
  cnameTarget: z.string(),
  txtRecord: z.string(),
  txtVerified: z.boolean(),
  txtVerifiedAt: z.coerce.date().nullable(),
  verified: z.boolean(),
  verifiedAt: z.coerce.date().nullable(),
  lastCheckedAt: z.coerce.date().nullable(),
  lastError: z.string().nullable(),
  /**
   * Form `collectionId` (share-URL slug) to serve when a visitor hits the
   * bare hostname with no path. `null` means the bare host 404s. See the
   * schema docstring for why we store the slug rather than the UUID.
   */
  defaultFormSlug: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})
export type CustomDomain = z.infer<typeof CustomDomain>

/** Loose hostname guard: 1-253 chars, labels of 1-63 chars, no protocol. */
const hostnameRegex = /^(?=.{1,253}$)(?!-)(?:[a-z0-9-]{1,63}(?<!-)\.)+[a-z]{2,63}$/i

/**
 * Apex / bare-root domains (`example.com`) can't host a CNAME record per
 * RFC 1034 — the DNS spec forbids CNAME at the zone root because it would
 * conflict with the SOA/NS records that have to live there. Almost every
 * registrar (GoDaddy, Namecheap, etc.) enforces this and will reject a
 * CNAME on `@`.
 *
 * Custom forms have to live on a subdomain like `forms.example.com`. We
 * detect the common apex case as "fewer than 3 labels" which catches every
 * single-part TLD (`.com`, `.games`, `.io`, …). Multi-part TLDs like
 * `example.co.uk` slip through this heuristic, but those users will hit
 * the same error from their registrar with a clear message either way.
 */
const looksLikeApex = (hostname: string): boolean =>
  hostname.split('.').length < 3

export const CreateCustomDomainParams = z.object({
  workspaceId: z.string(),
  hostname: z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(253)
    // Strip a `http(s)://` or trailing slash if the user pasted a URL.
    .transform((v) => v.replace(/^https?:\/\//, '').replace(/\/.*$/, ''))
    .refine((v) => hostnameRegex.test(v), {
      message: 'Enter a valid hostname (e.g. forms.example.com).',
    })
    .refine((v) => !looksLikeApex(v), {
      message:
        'Apex domains (like example.com) can\'t be used because of a DNS-spec limitation. Use a subdomain such as forms.example.com instead.',
    }),
})
export type CreateCustomDomainParams = z.infer<typeof CreateCustomDomainParams>

export const DeleteCustomDomainParams = z.object({ id: z.string() })
export type DeleteCustomDomainParams = z.infer<typeof DeleteCustomDomainParams>

export const VerifyCustomDomainParams = z.object({ id: z.string() })
export type VerifyCustomDomainParams = z.infer<typeof VerifyCustomDomainParams>

/**
 * Update which form is served at the bare hostname. Pass `formSlug: null`
 * to clear the default and return the domain to "404 on root" behavior.
 */
export const SetDefaultFormParams = z.object({
  id: z.string(),
  formSlug: z.string().min(1).max(64).nullable(),
})
export type SetDefaultFormParams = z.infer<typeof SetDefaultFormParams>
